import type { ConfiguratorState, OutlineNotchEdge } from './configurator-math';

import {
  getProductOutlineNotches,
  getProductOutlineSettings,
  getSelectedOptions,
} from './configurator-math';

export type OutlinePoint = { xMm: number; yMm: number };
export type OutlineEdgeName = 'top' | 'right' | 'bottom' | 'left';
export type OutlineCornerType = 'square' | 'radius' | 'chamfer' | 'chamfer-irregular';

export type OutlineCorner = {
  id: string;
  kind: 'outer' | 'inner';
  xMm: number;
  yMm: number;
  edge?: OutlineEdgeName | OutlineNotchEdge;
  treatment: {
    type: OutlineCornerType;
    radiusMm: number;
    chamferXMm: number;
    chamferYMm: number;
  };
  sourceId?: string;
};

export type OutlineSegment = {
  id: string;
  from: OutlinePoint;
  to: OutlinePoint;
  edge?: OutlineEdgeName | OutlineNotchEdge;
};

export type OutlineGeometry = {
  points: OutlinePoint[];
  corners: OutlineCorner[];
  segments: OutlineSegment[];
  widthMm: number;
  lengthMm: number;
};

type CornerKey = 'TopLeft' | 'TopRight' | 'BottomRight' | 'BottomLeft';

const cornerIds: Record<CornerKey, string> = {
  TopLeft: 'outer-top-left',
  TopRight: 'outer-top-right',
  BottomRight: 'outer-bottom-right',
  BottomLeft: 'outer-bottom-left',
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getNotchMouthRadius(
  notch: ReturnType<typeof getProductOutlineNotches>[number],
  edgeAxisSizeMm: number,
) {
  const startMm = notch.centerMm - notch.widthMm / 2;
  const endMm = notch.centerMm + notch.widthMm / 2;
  const availableBeforeMm = startMm;
  const availableAfterMm = edgeAxisSizeMm - endMm;

  return clamp(
    notch.mouthRadiusMm,
    0,
    Math.max(0, Math.min(notch.depthMm * 0.45, availableBeforeMm, availableAfterMm)),
  );
}

function getCornerType(config: ConfiguratorState, corner: CornerKey): OutlineCornerType {
  const value = config[`corner${corner}Type` as keyof ConfiguratorState];
  return value === 'radius' || value === 'chamfer' || value === 'chamfer-irregular'
    ? value
    : 'square';
}

function getCornerNumber(config: ConfiguratorState, key: keyof ConfiguratorState) {
  const value = config[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function getCornerTreatment(config: ConfiguratorState, corner: CornerKey) {
  const explicitType = getCornerType(config, corner);
  const outline = getProductOutlineSettings(config);
  const fallbackRadius = Math.max(config.edgeRadiusMm ?? 0, outline.outerRadiusMm ?? 0);
  const type: OutlineCornerType =
    explicitType !== 'square' ? explicitType : fallbackRadius > 0 ? 'radius' : 'square';
  const sizeMm =
    explicitType !== 'square'
      ? getCornerNumber(config, `corner${corner}Mm` as keyof ConfiguratorState)
      : fallbackRadius;
  const chamferXMm =
    type === 'chamfer-irregular'
      ? getCornerNumber(config, `corner${corner}ChamferXMm` as keyof ConfiguratorState)
      : sizeMm;
  const chamferYMm =
    type === 'chamfer-irregular'
      ? getCornerNumber(config, `corner${corner}ChamferYMm` as keyof ConfiguratorState)
      : sizeMm;

  return {
    type,
    radiusMm: type === 'radius' ? Math.max(0, sizeMm) : 0,
    chamferXMm: type === 'chamfer' || type === 'chamfer-irregular' ? Math.max(0, chamferXMm) : 0,
    chamferYMm: type === 'chamfer' || type === 'chamfer-irregular' ? Math.max(0, chamferYMm) : 0,
  };
}

function addPoint(points: OutlinePoint[], point: OutlinePoint) {
  const previous = points[points.length - 1];
  if (
    previous &&
    Math.abs(previous.xMm - point.xMm) < 0.001 &&
    Math.abs(previous.yMm - point.yMm) < 0.001
  ) {
    return;
  }
  points.push(point);
}

function addArc(
  points: OutlinePoint[],
  centerX: number,
  centerY: number,
  radius: number,
  start: number,
  end: number,
  steps = 10,
) {
  if (radius <= 0.001) return;
  for (let index = 0; index <= steps; index += 1) {
    const angle = start + ((end - start) * index) / steps;
    addPoint(points, {
      xMm: centerX + Math.cos(angle) * radius,
      yMm: centerY + Math.sin(angle) * radius,
    });
  }
}

function outerStartOffset(treatment: ReturnType<typeof getCornerTreatment>) {
  if (treatment.type === 'radius') return treatment.radiusMm;
  if (treatment.type === 'chamfer' || treatment.type === 'chamfer-irregular')
    return treatment.chamferXMm;
  return 0;
}

function addOuterCorner(
  points: OutlinePoint[],
  config: ConfiguratorState,
  corner: CornerKey,
  widthMm: number,
  lengthMm: number,
  corners: OutlineCorner[],
) {
  const treatment = getCornerTreatment(config, corner);
  const maxRadius = Math.min(widthMm, lengthMm) / 2;
  const radius = clamp(treatment.radiusMm, 0, maxRadius);
  const chamferX = clamp(treatment.chamferXMm, 0, widthMm / 2);
  const chamferY = clamp(treatment.chamferYMm, 0, lengthMm / 2);

  const cornerPoint: Record<CornerKey, OutlinePoint> = {
    TopLeft: { xMm: 0, yMm: 0 },
    TopRight: { xMm: widthMm, yMm: 0 },
    BottomRight: { xMm: widthMm, yMm: lengthMm },
    BottomLeft: { xMm: 0, yMm: lengthMm },
  };
  corners.push({
    id: cornerIds[corner],
    kind: 'outer',
    ...cornerPoint[corner],
    treatment: { ...treatment, radiusMm: radius, chamferXMm: chamferX, chamferYMm: chamferY },
  });

  if (corner === 'TopRight') {
    if (radius > 0) {
      addPoint(points, { xMm: widthMm - radius, yMm: 0 });
      addArc(points, widthMm - radius, radius, radius, -Math.PI / 2, 0);
    } else if (chamferX > 0 || chamferY > 0) {
      addPoint(points, { xMm: widthMm - chamferX, yMm: 0 });
      addPoint(points, { xMm: widthMm, yMm: chamferY });
    } else {
      addPoint(points, { xMm: widthMm, yMm: 0 });
    }
    return;
  }

  if (corner === 'BottomRight') {
    if (radius > 0) {
      addPoint(points, { xMm: widthMm, yMm: lengthMm - radius });
      addArc(points, widthMm - radius, lengthMm - radius, radius, 0, Math.PI / 2);
    } else if (chamferX > 0 || chamferY > 0) {
      addPoint(points, { xMm: widthMm, yMm: lengthMm - chamferY });
      addPoint(points, { xMm: widthMm - chamferX, yMm: lengthMm });
    } else {
      addPoint(points, { xMm: widthMm, yMm: lengthMm });
    }
    return;
  }

  if (corner === 'BottomLeft') {
    if (radius > 0) {
      addPoint(points, { xMm: radius, yMm: lengthMm });
      addArc(points, radius, lengthMm - radius, radius, Math.PI / 2, Math.PI);
    } else if (chamferX > 0 || chamferY > 0) {
      addPoint(points, { xMm: chamferX, yMm: lengthMm });
      addPoint(points, { xMm: 0, yMm: lengthMm - chamferY });
    } else {
      addPoint(points, { xMm: 0, yMm: lengthMm });
    }
    return;
  }

  if (radius > 0) {
    addPoint(points, { xMm: 0, yMm: radius });
    addArc(points, radius, radius, radius, Math.PI, Math.PI * 1.5);
  } else if (chamferX > 0 || chamferY > 0) {
    addPoint(points, { xMm: 0, yMm: chamferY });
    addPoint(points, { xMm: chamferX, yMm: 0 });
  } else {
    addPoint(points, { xMm: 0, yMm: 0 });
  }
}

function addTopNotch(
  points: OutlinePoint[],
  notch: ReturnType<typeof getProductOutlineNotches>[number],
  widthMm: number,
  corners: OutlineCorner[],
) {
  const left = notch.centerMm - notch.widthMm / 2;
  const right = notch.centerMm + notch.widthMm / 2;
  const depth = notch.depthMm;
  const radius = clamp(notch.radiusMm, 0, Math.min(notch.widthMm, depth) / 2);
  const mouthRadius = getNotchMouthRadius(notch, widthMm);

  addPoint(points, { xMm: left - mouthRadius, yMm: 0 });
  if (mouthRadius > 0) {
    addArc(points, left - mouthRadius, mouthRadius, mouthRadius, -Math.PI / 2, 0);
  }
  if (radius > 0) {
    addPoint(points, { xMm: left, yMm: depth - radius });
    addArc(points, left + radius, depth - radius, radius, Math.PI, Math.PI / 2);
    addPoint(points, { xMm: right - radius, yMm: depth });
    addArc(points, right - radius, depth - radius, radius, Math.PI / 2, 0);
  } else {
    addPoint(points, { xMm: left, yMm: depth });
    addPoint(points, { xMm: right, yMm: depth });
  }
  if (mouthRadius > 0) {
    addPoint(points, { xMm: right, yMm: mouthRadius });
    addArc(points, right + mouthRadius, mouthRadius, mouthRadius, Math.PI, Math.PI * 1.5);
  } else {
    addPoint(points, { xMm: right, yMm: 0 });
  }

  corners.push(
    {
      id: `${notch.id}-mouth-left`,
      kind: 'outer',
      xMm: left,
      yMm: 0,
      edge: 'top',
      sourceId: notch.id,
      treatment: {
        type: mouthRadius > 0 ? 'radius' : 'square',
        radiusMm: mouthRadius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-mouth-right`,
      kind: 'outer',
      xMm: right,
      yMm: 0,
      edge: 'top',
      sourceId: notch.id,
      treatment: {
        type: mouthRadius > 0 ? 'radius' : 'square',
        radiusMm: mouthRadius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-inner-left`,
      kind: 'inner',
      xMm: left,
      yMm: depth,
      edge: 'top',
      sourceId: notch.id,
      treatment: {
        type: radius > 0 ? 'radius' : 'square',
        radiusMm: radius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-inner-right`,
      kind: 'inner',
      xMm: right,
      yMm: depth,
      edge: 'top',
      sourceId: notch.id,
      treatment: {
        type: radius > 0 ? 'radius' : 'square',
        radiusMm: radius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
  );
}

function addBottomNotch(
  points: OutlinePoint[],
  notch: ReturnType<typeof getProductOutlineNotches>[number],
  widthMm: number,
  lengthMm: number,
  corners: OutlineCorner[],
) {
  const left = notch.centerMm - notch.widthMm / 2;
  const right = notch.centerMm + notch.widthMm / 2;
  const y = lengthMm - notch.depthMm;
  const radius = clamp(notch.radiusMm, 0, Math.min(notch.widthMm, notch.depthMm) / 2);
  const mouthRadius = getNotchMouthRadius(notch, widthMm);

  addPoint(points, { xMm: right + mouthRadius, yMm: lengthMm });
  if (mouthRadius > 0) {
    addArc(points, right + mouthRadius, lengthMm - mouthRadius, mouthRadius, Math.PI / 2, Math.PI);
  }
  if (radius > 0) {
    addPoint(points, { xMm: right, yMm: y + radius });
    addArc(points, right - radius, y + radius, radius, 0, -Math.PI / 2);
    addPoint(points, { xMm: left + radius, yMm: y });
    addArc(points, left + radius, y + radius, radius, -Math.PI / 2, -Math.PI);
  } else {
    addPoint(points, { xMm: right, yMm: y });
    addPoint(points, { xMm: left, yMm: y });
  }
  if (mouthRadius > 0) {
    addPoint(points, { xMm: left, yMm: lengthMm - mouthRadius });
    addArc(points, left - mouthRadius, lengthMm - mouthRadius, mouthRadius, 0, Math.PI / 2);
  } else {
    addPoint(points, { xMm: left, yMm: lengthMm });
  }

  corners.push(
    {
      id: `${notch.id}-mouth-right`,
      kind: 'outer',
      xMm: right,
      yMm: lengthMm,
      edge: 'bottom',
      sourceId: notch.id,
      treatment: {
        type: mouthRadius > 0 ? 'radius' : 'square',
        radiusMm: mouthRadius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-mouth-left`,
      kind: 'outer',
      xMm: left,
      yMm: lengthMm,
      edge: 'bottom',
      sourceId: notch.id,
      treatment: {
        type: mouthRadius > 0 ? 'radius' : 'square',
        radiusMm: mouthRadius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-inner-right`,
      kind: 'inner',
      xMm: right,
      yMm: y,
      edge: 'bottom',
      sourceId: notch.id,
      treatment: {
        type: radius > 0 ? 'radius' : 'square',
        radiusMm: radius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-inner-left`,
      kind: 'inner',
      xMm: left,
      yMm: y,
      edge: 'bottom',
      sourceId: notch.id,
      treatment: {
        type: radius > 0 ? 'radius' : 'square',
        radiusMm: radius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
  );
}

function addRightNotch(
  points: OutlinePoint[],
  notch: ReturnType<typeof getProductOutlineNotches>[number],
  widthMm: number,
  lengthMm: number,
  corners: OutlineCorner[],
) {
  const top = notch.centerMm - notch.widthMm / 2;
  const bottom = notch.centerMm + notch.widthMm / 2;
  const x = widthMm - notch.depthMm;
  const radius = clamp(notch.radiusMm, 0, Math.min(notch.widthMm, notch.depthMm) / 2);
  const mouthRadius = getNotchMouthRadius(notch, lengthMm);

  addPoint(points, { xMm: widthMm, yMm: top - mouthRadius });
  if (mouthRadius > 0) {
    addArc(points, widthMm - mouthRadius, top - mouthRadius, mouthRadius, 0, Math.PI / 2);
  }
  if (radius > 0) {
    addPoint(points, { xMm: x + radius, yMm: top });
    addArc(points, x + radius, top + radius, radius, -Math.PI / 2, -Math.PI);
    addPoint(points, { xMm: x, yMm: bottom - radius });
    addArc(points, x + radius, bottom - radius, radius, Math.PI, Math.PI / 2);
  } else {
    addPoint(points, { xMm: x, yMm: top });
    addPoint(points, { xMm: x, yMm: bottom });
  }
  if (mouthRadius > 0) {
    addPoint(points, { xMm: widthMm - mouthRadius, yMm: bottom });
    addArc(points, widthMm - mouthRadius, bottom + mouthRadius, mouthRadius, -Math.PI / 2, 0);
  } else {
    addPoint(points, { xMm: widthMm, yMm: bottom });
  }

  corners.push(
    {
      id: `${notch.id}-mouth-top`,
      kind: 'outer',
      xMm: widthMm,
      yMm: top,
      edge: 'right',
      sourceId: notch.id,
      treatment: {
        type: mouthRadius > 0 ? 'radius' : 'square',
        radiusMm: mouthRadius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-mouth-bottom`,
      kind: 'outer',
      xMm: widthMm,
      yMm: bottom,
      edge: 'right',
      sourceId: notch.id,
      treatment: {
        type: mouthRadius > 0 ? 'radius' : 'square',
        radiusMm: mouthRadius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-inner-top`,
      kind: 'inner',
      xMm: x,
      yMm: top,
      edge: 'right',
      sourceId: notch.id,
      treatment: {
        type: radius > 0 ? 'radius' : 'square',
        radiusMm: radius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-inner-bottom`,
      kind: 'inner',
      xMm: x,
      yMm: bottom,
      edge: 'right',
      sourceId: notch.id,
      treatment: {
        type: radius > 0 ? 'radius' : 'square',
        radiusMm: radius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
  );
}

function addLeftNotch(
  points: OutlinePoint[],
  notch: ReturnType<typeof getProductOutlineNotches>[number],
  lengthMm: number,
  corners: OutlineCorner[],
) {
  const top = notch.centerMm - notch.widthMm / 2;
  const bottom = notch.centerMm + notch.widthMm / 2;
  const x = notch.depthMm;
  const radius = clamp(notch.radiusMm, 0, Math.min(notch.widthMm, notch.depthMm) / 2);
  const mouthRadius = getNotchMouthRadius(notch, lengthMm);

  addPoint(points, { xMm: 0, yMm: bottom + mouthRadius });
  if (mouthRadius > 0) {
    addArc(points, mouthRadius, bottom + mouthRadius, mouthRadius, Math.PI, Math.PI * 1.5);
  }
  if (radius > 0) {
    addPoint(points, { xMm: x - radius, yMm: bottom });
    addArc(points, x - radius, bottom - radius, radius, Math.PI / 2, 0);
    addPoint(points, { xMm: x, yMm: top + radius });
    addArc(points, x - radius, top + radius, radius, 0, -Math.PI / 2);
  } else {
    addPoint(points, { xMm: x, yMm: bottom });
    addPoint(points, { xMm: x, yMm: top });
  }
  if (mouthRadius > 0) {
    addPoint(points, { xMm: mouthRadius, yMm: top });
    addArc(points, mouthRadius, top - mouthRadius, mouthRadius, Math.PI / 2, Math.PI);
  } else {
    addPoint(points, { xMm: 0, yMm: top });
  }

  corners.push(
    {
      id: `${notch.id}-mouth-bottom`,
      kind: 'outer',
      xMm: 0,
      yMm: bottom,
      edge: 'left',
      sourceId: notch.id,
      treatment: {
        type: mouthRadius > 0 ? 'radius' : 'square',
        radiusMm: mouthRadius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-mouth-top`,
      kind: 'outer',
      xMm: 0,
      yMm: top,
      edge: 'left',
      sourceId: notch.id,
      treatment: {
        type: mouthRadius > 0 ? 'radius' : 'square',
        radiusMm: mouthRadius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-inner-bottom`,
      kind: 'inner',
      xMm: x,
      yMm: bottom,
      edge: 'left',
      sourceId: notch.id,
      treatment: {
        type: radius > 0 ? 'radius' : 'square',
        radiusMm: radius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
    {
      id: `${notch.id}-inner-top`,
      kind: 'inner',
      xMm: x,
      yMm: top,
      edge: 'left',
      sourceId: notch.id,
      treatment: {
        type: radius > 0 ? 'radius' : 'square',
        radiusMm: radius,
        chamferXMm: 0,
        chamferYMm: 0,
      },
    },
  );
}

function buildSegments(points: OutlinePoint[]): OutlineSegment[] {
  return points.map((from, index) => ({
    id: `edge-${index + 1}`,
    from,
    to: points[(index + 1) % points.length],
  }));
}

export function buildOutlineGeometry(config: ConfiguratorState): OutlineGeometry {
  const { product } = getSelectedOptions(config);
  const widthMm = Math.max(config.widthMm, 1);
  const lengthMm = Math.max(config.lengthMm, 1);
  const points: OutlinePoint[] = [];
  const corners: OutlineCorner[] = [];
  const taper =
    product.visual.kind === 'trapezoid'
      ? widthMm * 0.14
      : product.visual.kind === 'freeform'
        ? widthMm * 0.08
        : 0;

  if (taper > 0) {
    points.push(
      { xMm: taper, yMm: 0 },
      { xMm: widthMm - taper * 0.45, yMm: 0 },
      { xMm: widthMm, yMm: lengthMm },
      { xMm: 0, yMm: lengthMm },
    );
    return { points, corners, segments: buildSegments(points), widthMm, lengthMm };
  }

  const tl = getCornerTreatment(config, 'TopLeft');
  addPoint(points, { xMm: outerStartOffset(tl), yMm: 0 });

  getProductOutlineNotches(config)
    .filter(notch => notch.edge === 'top' && notch.widthMm > 0 && notch.depthMm > 0)
    .sort((a, b) => a.centerMm - b.centerMm)
    .forEach(notch => addTopNotch(points, notch, widthMm, corners));

  addOuterCorner(points, config, 'TopRight', widthMm, lengthMm, corners);

  getProductOutlineNotches(config)
    .filter(notch => notch.edge === 'right' && notch.widthMm > 0 && notch.depthMm > 0)
    .sort((a, b) => a.centerMm - b.centerMm)
    .forEach(notch => addRightNotch(points, notch, widthMm, lengthMm, corners));

  addOuterCorner(points, config, 'BottomRight', widthMm, lengthMm, corners);

  getProductOutlineNotches(config)
    .filter(notch => notch.edge === 'bottom' && notch.widthMm > 0 && notch.depthMm > 0)
    .sort((a, b) => b.centerMm - a.centerMm)
    .forEach(notch => addBottomNotch(points, notch, widthMm, lengthMm, corners));

  addOuterCorner(points, config, 'BottomLeft', widthMm, lengthMm, corners);

  getProductOutlineNotches(config)
    .filter(notch => notch.edge === 'left' && notch.widthMm > 0 && notch.depthMm > 0)
    .sort((a, b) => b.centerMm - a.centerMm)
    .forEach(notch => addLeftNotch(points, notch, lengthMm, corners));

  addOuterCorner(points, config, 'TopLeft', widthMm, lengthMm, corners);

  return { points, corners, segments: buildSegments(points), widthMm, lengthMm };
}

export function outlinePointToScreen(
  point: OutlinePoint,
  drawing: { left: number; top: number; width: number; height: number },
  geometry: Pick<OutlineGeometry, 'widthMm' | 'lengthMm'>,
) {
  return {
    x: drawing.left + (point.xMm / geometry.widthMm) * drawing.width,
    y: drawing.top + (point.yMm / geometry.lengthMm) * drawing.height,
  };
}

export function outlineGeometryToScreenPoints(
  geometry: OutlineGeometry,
  drawing: { left: number; top: number; width: number; height: number },
) {
  return geometry.points.map(point => {
    const screen = outlinePointToScreen(point, drawing, geometry);
    return [screen.x, screen.y] as [number, number];
  });
}
